// This is the main wrapper for the Direct Wines API services
// It manages which or the old or new services is called based upon supplied config
import { globals } from 'common-ui';
import { initAtgLegacyApi } from './directWinesApis/atgLegacyApi';
import { initAwsTempApi } from './directWinesApis/awsTempApi';
import { initWebApi } from './directWinesApis/webApi';
import { initWebAuthApi } from './directWinesApis/webAuthApi';
import { initBffApi } from './directWinesApis/bffApi';
import { transformAtgCartToVineCart, transformAtgProductToVineProduct, transformAwsCartToVineCart } from './transforms';
import type { createCartPayload, ProductProps } from 'tsconfig/types';

export type CommerceApiConfig = {
  //Legacy ATG
  atgApiPrefix: string; //host
  atgApiExtraHeaders?: Record<string, string>;
  //Temp AWS
  awsApiPrefix: string; //apiHost
  awsApiExtraHeaders?: Record<string, string>;
  brandToken?: string;
  userToken?: string;
  //Web/Auth API
  webApiPrefix: string; //host
  webApiHost: string; //host
  webApiExtraHeaders?: Record<string, string>;
  //misc
  enableLogging?: boolean;
};

export type CommerceApiOverrideConfig = {
  host?: string;
  //Legacy ATG
  atgApiPrefix?: string; //host
  atgApiExtraHeaders?: Record<string, string>;
  //Temp AWS
  awsApiPrefix?: string; //apiHost
  awsApiExtraHeaders?: Record<string, string>;
  brandToken?: string;
  userToken?: string;
  //Web/Auth API
  webApiPrefix?: string; //host
  webApiHost?: string; //host
  webApiExtraHeaders?: Record<string, string>;
  //misc
  enableLogging?: boolean;
};

export const initCommerceApi = (config: CommerceApiConfig): ReturnType<typeof createCommerceApi> =>
  createCommerceApi(config);

function createCommerceApi(config: CommerceApiConfig) {
  const atgLegacyApi: any = initAtgLegacyApi({
    urlPrefix: config.atgApiPrefix,
    extraHeaders: config.atgApiExtraHeaders,
    enableLogging: config.enableLogging,
  });
  const awsTempApi = initAwsTempApi({
    urlPrefix: config.awsApiPrefix,
    extraHeaders: config.awsApiExtraHeaders,
    enableLogging: config.enableLogging,
    brandToken: config.brandToken,
    userToken: config.userToken,
  });
  const webApi = initWebApi({
    urlPrefix: config.webApiPrefix,
    extraHeaders: config.webApiExtraHeaders,
    host: config.webApiHost,
    countryBrand: globals.countryBrand as string,
  });
  const authApi = initWebAuthApi({
    urlPrefix: config.webApiPrefix,
    extraHeaders: config.webApiExtraHeaders,
    // brandDomain: config.webApiBrandDomain
    host: config.webApiHost,
  });
  const bffApi = initBffApi({
    urlPrefix: '/next/api',
    extraHeaders: config.webApiExtraHeaders,
    // extraHeaders?: Record<string, string>;
  });

  return {
    Auth: {
      getAnonymousToken: authApi.Auth.getAnonymousToken,
      reload: authApi.Auth.reload,
      refresh: authApi.Auth.refresh,
    },
    User: {
      get: atgLegacyApi.User.get,
      permission: atgLegacyApi.User.permission,
      softLogout: atgLegacyApi.User.softLogout,
      hardLogout: atgLegacyApi.User.hardLogout,
      hardLogoutViaAPI: atgLegacyApi.User.hardLogoutViaAPI,
      // This API is only to set user location in US Brands
      userSetLocation: atgLegacyApi.User.userSetLocation,
      getFavourites: atgLegacyApi.User.getFavourites,
      getRecentlyBrowsed: atgLegacyApi.User.getRecentlyBrowsed,
      getRatedProducts: atgLegacyApi.User.getRatedProducts,
      getPurchasedProducts: atgLegacyApi.User.getPurchasedProducts,
    },
    Cart: {
      get: async () => {
        const atgCartResponse = await atgLegacyApi.Cart.get();
        return transformAtgCartToVineCart(atgCartResponse);
      },
      add: async (productSku: string, quantity: string) => {
        const atgCartResponse = await atgLegacyApi.Cart.add(productSku, quantity);
        return transformAtgCartToVineCart(atgCartResponse);
      },
      batch: async (data: any) => {
        const atgCartResponse = await atgLegacyApi.Cart.batch(data);
        return transformAtgCartToVineCart(atgCartResponse);
      },
      getCartOffer: atgLegacyApi.Cart.getCartOffer,
    },
    CartPreferences: {
      createNewCart: async (data?: createCartPayload) => {
        const webApiResponse = await webApi.CartPreferences.createNewCart(data);
        return transformAwsCartToVineCart(webApiResponse);
      },
      addItemToCart: async (params: any) => {
        const webApiResponse = await webApi.CartPreferences.addItemToCart(params);
        return transformAwsCartToVineCart(webApiResponse);
      },
      deleteItemFromCart: async (params: any) => {
        const webApiResponse = await webApi.CartPreferences.deleteItemFromCart(params);
        return transformAwsCartToVineCart(webApiResponse);
      },
      updateCartItem: async (params: any, data: any) => {
        const webApiResponse = await webApi.CartPreferences.updateCartItem(params, data);
        return transformAwsCartToVineCart(webApiResponse);
      },
      getMyCart: async (params: any) => {
        const webApiResponse = await webApi.CartPreferences.getMyCart(params);
        return transformAwsCartToVineCart(webApiResponse);
      },
      cartActions: async (params: any) => {
        const webApiResponse = await webApi.CartPreferences.cartActions(params);
        return transformAwsCartToVineCart(webApiResponse);
      },
    },
    BrandPreferences: {
      get: awsTempApi.BrandPreferences.get,
    },
    UserPreferences: {
      get: awsTempApi.UserPreferences.get,
      post: awsTempApi.UserPreferences.post,
      put: awsTempApi.UserPreferences.put,
    },
    MyUserPreferences: {
      get: awsTempApi.MyUserPreferences.get,
      post: awsTempApi.MyUserPreferences.post,
      put: awsTempApi.MyUserPreferences.put,
    },
    Product: {
      get: async (countryBrand: string, itemCode: string) => {
        const awsProductResponse = await awsTempApi.Product.get(countryBrand, itemCode);
        return transformAtgProductToVineProduct(awsProductResponse as ProductProps);
      },
      getAllProductData: bffApi.Product.getAllProductData,
      getOffers: atgLegacyApi.Product.getOffers,
      getRecentlyViewed: atgLegacyApi.Product.getRecentlyViewed,
      addItemToRecentlyViewed: atgLegacyApi.Product.addItemToRecentlyViewed,
      compliance: bffApi.Product.compliance,
    },
    Promotions: {
      getProductPromotions: atgLegacyApi.Promotions.getProductPromotions,
    },
    Subscription: {
      get: awsTempApi.Subscription.get,
      updateAutoRenew: awsTempApi.Subscription.updateAutoRenew,
      getCancelReason: awsTempApi.Subscription.getCancelReason,
      getCancelOffers: awsTempApi.Subscription.getCancelOffers,
      applyUnlimitedOffer: awsTempApi.Subscription.applyUnlimitedOffer,
    },
    WinePlan: {
      getUpcomingCaseDetails: atgLegacyApi.WinePlan.getUpcomingCaseDetails,
      getProductCaseDetails: atgLegacyApi.WinePlan.getProductCaseDetails,
      getProductItemDetails: atgLegacyApi.WinePlan.getProductItemDetails,
      getSupportedFrequencies: atgLegacyApi.WinePlan.getSupportedFrequencies,
      putSupportedFrequencies: atgLegacyApi.WinePlan.putSupportedFrequencies,
      getDeliveryMethods: atgLegacyApi.WinePlan.getDeliveryMethods,
      getDeliveryDates: atgLegacyApi.WinePlan.getDeliveryDates,
      updateDeliveryDate: atgLegacyApi.WinePlan.updateDeliveryDate,
      getPaymentMethods: atgLegacyApi.WinePlan.getPaymentMethods,
      getSubscriptionCaseDetails: atgLegacyApi.WinePlan.getSubscriptionCaseDetails,
      getOrderHistoryDetails: atgLegacyApi.WinePlan.getOrderHistoryDetails,
      getUserDefaultAddresses: atgLegacyApi.WinePlan.getUserDefaultAddresses,
      getAddressByPincode: atgLegacyApi.WinePlan.getAddressByPincode,
      saveDeliveryAddress: atgLegacyApi.WinePlan.saveDeliveryAddress,
      useDeliveryAddress: atgLegacyApi.WinePlan.useDeliveryAddress,
      getStoresByPincode: atgLegacyApi.WinePlan.getStoresByPincode,
      selectStoreDeliveryAddress: atgLegacyApi.WinePlan.selectStoreDeliveryAddress,
      getSavingsAccount: atgLegacyApi.WinePlan.getSavingsAccount,
      getBalanceForAccount: atgLegacyApi.WinePlan.getBalanceForAccount,
      payWinePlanwithWineReward: atgLegacyApi.WinePlan.payWinePlanwithWineReward,
      getCaseOptions: atgLegacyApi.WinePlan.getCaseOptions,
      swapWineSubscription: atgLegacyApi.WinePlan.swapWineSubscription,
      pushBackSaveOfferWineSubscription: atgLegacyApi.WinePlan.pushBackSaveOfferWineSubscription,
      applyPushBackSaveOfferWineSubscription: atgLegacyApi.WinePlan.applyPushBackSaveOfferWineSubscription,
      pushBackSaveOfferCheckWineSubscription: atgLegacyApi.WinePlan.pushBackSaveOfferCheckWineSubscription,
      addFlexItem: atgLegacyApi.WinePlan.addFlexItem,
      removeFlexItem: atgLegacyApi.WinePlan.removeFlexItem,
      updateFlexItem: atgLegacyApi.WinePlan.updateFlexItem,
      getFlexDetails: atgLegacyApi.WinePlan.getFlexDetails,
      saveFlexOrder: atgLegacyApi.WinePlan.saveFlexOrder,
      getCancelReason: atgLegacyApi.WinePlan.getCancelReason,
      getCancelSaveOffers: atgLegacyApi.WinePlan.getCancelSaveOffers,
      putCancelFeedback: atgLegacyApi.WinePlan.putCancelFeedback,
      addCancelSaveOffer: atgLegacyApi.WinePlan.addCancelSaveOffer,
      cancelSubscription: atgLegacyApi.WinePlan.cancelSubscription,
    },
    Checkout: {
      setDeliveryGroups: webApi.Checkout.setDeliveryGroups,
      updateDeliveryGroups: webApi.Checkout.updateDeliveryGroups,
      placeOrder: webApi.Checkout.placeOrder,
      validateCapture: webApi.Checkout.validateCapture,
      setup3DS: webApi.Checkout.setup3DS,
      getPaymentClientToken: webApi.Checkout.getPaymentClientToken,
      getPaymentOptions: atgLegacyApi.Checkout.getPaymentOptions,
      validateApplePaySession: atgLegacyApi.Checkout.validateApplePaySession,
      getGiftCardBalance: atgLegacyApi.Checkout.getGiftCardBalance,
      getOrderDetails: webApi.Checkout.getOrderDetails,
      getFuturePaymentsByCart: atgLegacyApi.Checkout.getFuturePaymentsByCart,
    },
    Account: {
      checkEmail: atgLegacyApi.Account.checkEmail,
    },
  };
}

//initialise the APi using environmental vars (via globals)
export const initCommerceApiWithGlobals = (
  overrideConfig: CommerceApiOverrideConfig = {},
): ReturnType<typeof initCommerceApi> =>
  initCommerceApi({
    //Legacy ATG
    atgApiPrefix: globals.atgApiPrefix as string,
    //Temp AWS
    awsApiPrefix: globals.awsApiPrefix as string,
    //Web/Auth API
    webApiPrefix: globals.authAndWebApiPrefix as string,
    webApiHost: globals.authAndWebApiHost as string,
    // webApiBrandDomain: globals.brandDomain as string,
    ...overrideConfig,
  });
